import chroma from 'chroma-js';
import { roundTo, color2css } from '../../../../../utilities';
import { componentNames } from '../components.constants';

import { IComponent } from '../IComponent';
import { IRenderer } from '../IRenderer';

interface DataImageProps {
  enabled: boolean;
  position: string;
  lightnessThreshold: number;
  format: any; // Format;
  style: any; // FontFormat;
  darkColor: string;
  lightColor: string;
  forceDraw: boolean;
  forceSign?: boolean; // deprecated
  quillValue: any;
  valueAlignment: string;
}

interface DataImageRenderContext {
  mode: 'vertical' | 'horizontal';
  value: number; // render value
  htmlStr: string; // quill container & content
  //   backgroundColor: string;
  // bar props
  x: number;
  y: number;
  width: number;
  height: number;
  // img props
  imgWidth: number;
  imgHeight: number;
  position: string;
  imgRotate: boolean;
  callbacks: any; // reference
  animation: {
    enabled: boolean;
    duration: number;
    delay: number;
  };
}

const alignmentToFlex = (alignment, rotation = 'top') => {
  const matrix = {
    top: {
      'top-left': ['flex-start', 'flex-start'],
      'top-center': ['center', 'flex-start'],
      'top-right': ['flex-end', 'flex-start'],
      'center-left': ['flex-start', 'center'],
      center: ['center', 'center'],
      'center-right': ['flex-end', 'center'],
      'bottom-left': ['flex-start', 'flex-end'],
      'bottom-center': ['center', 'flex-end'],
      'bottom-right': ['flex-end', 'flex-end'],
    },
    bottom: {
      'top-left': ['flex-start', 'flex-end'],
      'top-center': ['center', 'flex-end'],
      'top-right': ['flex-end', 'flex-end'],
      'center-left': ['flex-start', 'center'],
      center: ['center', 'center'],
      'center-right': ['flex-end', 'center'],
      'bottom-left': ['flex-start', 'flex-start'],
      'bottom-center': ['center', 'flex-start'],
      'bottom-right': ['flex-end', 'flex-start'],
    },
    left: {
      'top-left': ['flex-end', 'flex-start'],
      'top-center': ['flex-end', 'center'],
      'top-right': ['flex-end', 'flex-end'],
      'center-left': ['center', 'flex-start'],
      center: ['center', 'center'],
      'center-right': ['center', 'flex-end'],
      'bottom-left': ['flex-start', 'flex-start'],
      'bottom-center': ['flex-start', 'center'],
      'bottom-right': ['flex-start', 'flex-end'],
    },
    right: {
      'top-left': ['flex-start', 'flex-start'],
      'top-center': ['flex-start', 'center'],
      'top-right': ['flex-start', 'flex-end'],
      'center-left': ['center', 'flex-start'],
      center: ['center', 'center'],
      'center-right': ['center', 'flex-end'],
      'bottom-left': ['flex-end', 'flex-start'],
      'bottom-center': ['flex-end', 'center'],
      'bottom-right': ['flex-end', 'flex-end'],
    },
  };

  const [justifyContent, alignItems] = matrix[rotation][alignment];
  return { justifyContent, alignItems };
};

export const anchorToFlexClass = (position: string, direction: string) => {
  let classObj: any = {};
  if (direction === 'horizontal') {
    switch (position) {
      case 'center-left':
        return 'justify-start';
      case 'center-right':
        return 'justify-end';
      case 'center':
      default:
        return 'justify-center';
    }
  }
  if (direction === 'vertical') {
    switch (position) {
      case 'top-center':
        return 'items-start';
      case 'bottom-center':
        return 'items-end';
      case 'center':
      default:
        return 'items-center';
    }
  }
  return classObj;
};

export const anchorToFlexClasses = (position: string, direction: string) => {
  let classObj: any = {};
  if (direction === 'horizontal') {
    switch (position) {
      case 'center-left':
        classObj['justify-start'] = true;
        classObj['justify-center'] = false;
        classObj['justify-end'] = false;
        break;
      case 'center-right':
        classObj['justify-start'] = false;
        classObj['justify-center'] = false;
        classObj['justify-end'] = true;
        break;
      case 'center':
      default:
        classObj['justify-start'] = false;
        classObj['justify-center'] = true;
        classObj['justify-end'] = false;
        break;
    }
  }
  if (direction === 'vertical') {
    switch (position) {
      case 'top-center':
        classObj['items-start'] = true;
        classObj['items-center'] = false;
        classObj['items-end'] = false;
        break;
      case 'bottom-center':
        classObj['items-start'] = false;
        classObj['items-center'] = false;
        classObj['items-end'] = true;
        break;
      case 'center':
      default:
        classObj['items-start'] = false;
        classObj['items-center'] = true;
        classObj['items-end'] = false;
        break;
    }
  }
  return classObj;
};

const isTextInside = (textWidth, textHeight, areaWidth, areaHeight, isRotated: boolean = false): boolean => {
  if (isRotated) {
    return textWidth < areaHeight && textHeight < areaWidth;
  } else {
    return textWidth < areaWidth && textHeight < areaHeight;
  }
};

export class DataImage implements IComponent {
  componentName: componentNames.datalabel;
  props: DataImageProps;

  protected constructor(props: DataImageProps) {
    this.props = props;
  }

  public getChildren(): IComponent[] {
    return [];
  }

  public toJson() {
    throw new Error('Method not implemented.');
  }

  public render(renderer: IRenderer) {
    return renderer.render();
  }

  static create = (props: DataImageProps) => {
    return new DataImage(props);
  };

  public quillToHTML = () => {
    //
  };

  public getRenderConfig = (context: DataImageRenderContext) => {
    const { mode, htmlStr, position, value, imgRotate, x, y, width, callbacks, animation } = context;
    let { height } = context;

    // start
    let xO = Math.round(x);
    let yO = Math.round(y);

    let xA, yA, rectWidth, rectHeight, rotation;

    if (mode === 'vertical') {
      // offset
      //   const offset = isZero ? (value < 0 ? dataLabelOffset.top : dataLabelOffset.bottom) : value >= 0 ? dataLabelOffset.top : dataLabelOffset.bottom;

      // animated
      xA = Math.round(x);
      yA = Math.round(y);

      // size
      rectWidth = roundTo(width);
      rectHeight = roundTo(height);

      // alignment
      rotation = 'top';
      if (imgRotate) rotation = 'left';
      if (value < 0) {
        if (rotation === 'top') rotation = 'bottom';
        if (rotation === 'left') rotation = 'right';
      }
    } else {
      // offset
      //   const offset = isZero ? (value < 0 ? dataLabelOffset.right : dataLabelOffset.left) : value >= 0 ? dataLabelOffset.right : dataLabelOffset.left;

      // animated
      xA = Math.round(x);
      yA = Math.round(y);

      // size
      rectWidth = roundTo(width);
      rectHeight = roundTo(height);

      // alignment
      rotation = 'left';
      if (value < 0) {
        if (rotation === 'left') rotation = 'right';
      }
    }

    const imageData = {
      html: htmlStr,
      x: xO,
      y: yO,
      width: rectWidth, // mode === 'vertical' ? rectWidth : 0,
      height: rectHeight, // mode === 'horizontal' ? rectHeight : 0,
      callbacks,
      class: '',
      transform: imgRotate ? `translate(${xO}px, ${yO}px) rotate(180deg)` : `translate(${xO}px, ${yO}px)`,
      transformForce: imgRotate ? 'rotate(180deg)' : undefined,
      animate: {
        enabled: animation.enabled,
        delay: animation.delay,
        duration: animation.duration,
        width: rectWidth,
        height: rectHeight,
        writingMode: imgRotate ? 'vertical-rl' : undefined,
        transform: imgRotate ? `translate(${xA}px, ${yA}px) rotate(180deg)` : `translate(${xA}px, ${yA}px)`,
      },
    };

    return imageData;
  };
}
